import { useState } from "react";
import { X, UserPlus } from "lucide-react";
import api from "../services/api";
import { getUserInfoFromToken } from "../services/auth";
import { toast } from "react-toastify";

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {children}
  </div>
);

const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

const AddSingleCustomerModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({
    name: "",
    phoneNumber: "",
    location: "",
    email: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!form.phoneNumber.trim()) {
      toast.error("Phone number is required");
      return;
    }

    const { userId } = getUserInfoFromToken();
    setLoading(true);
    try {
      await api.post("/customers/create", {
        name: form.name.trim(),
        phoneNumber: form.phoneNumber.trim(),
        location: form.location.trim() || undefined,
        email: form.email.trim() || undefined,
        userId,
        status: true,
      });
      toast.success("Customer added successfully");
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to add customer"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-blue-600" />
            <h2 className="text-base font-semibold text-gray-800">
              Add Customer
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <Field label="Name" required>
            <input
              type="text"
              value={form.name}
              onChange={set("name")}
              placeholder="Customer name"
              className={inputClass}
              autoFocus
            />
          </Field>

          <Field label="Phone Number" required>
            <input
              type="text"
              value={form.phoneNumber}
              onChange={set("phoneNumber")}
              placeholder="e.g. 919495863481"
              className={inputClass}
            />
            <p className="mt-1 text-xs text-gray-400">
              Include country code without + (e.g. 91 for India, 971 for UAE)
            </p>
          </Field>

          <Field label="Location">
            <input
              type="text"
              value={form.location}
              onChange={set("location")}
              placeholder="City or area (optional)"
              className={inputClass}
            />
          </Field>

          <Field label="Email">
            <input
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="email@example.com (optional)"
              className={inputClass}
            />
          </Field>

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 transition-colors"
            >
              {loading ? "Adding…" : "Add Customer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSingleCustomerModal;
